import { Button, FormControlLabel, styled, Switch } from '@mui/material';
import { StopType } from 'harmony-constants';
import { L } from 'harmony-language';
import moment from 'moment-timezone';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useDraftsMutation } from '../../../../api/mutations/reworked/use-drafts-mutation';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { Load, OrganizationLocation } from '../../../../types';
import { stopsToOrganizationLocationIdPairs, toSiteDisplayName, toSiteDisplayNameFromOrganizationLocation } from '../../../../utils/data-mapping';
import { localDateTimeDisplay } from '../../../../utils/date-time-utils';
import { ModalFooter } from '../../../shared/containers/modal-footer';
import { AgisticsDateTimePicker } from '../../../shared/inputs/agistics-date-time-picker';
import { Select } from '../../../shared/inputs/select';
import { SelectCascadeInterval } from '../../../shared/inputs/select-cascade-interval';
import { CascadeIntervals } from '../cascade/use-cascade-stops';
import { AgisticsSelect, createAgisticsSelectElements } from '../../../shared/agistics-select';
import { AgisticsColors } from '../../../../styles/agistics-colors';
import { findUnknownPairsInCache, TravelTimesCache, updatePointlessPairs } from '../../../travel-times/use-travel-times';
import { useUser } from '../../../../api/queries/use-user';
import { useQueryClient } from '@tanstack/react-query';
import { useAddTravelTimes } from '../../../../api/mutations/reworked/add/use-add-travel-times';

const BulkModifyRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: '.5fr .5fr 2fr repeat(2, 1fr)',
    gridColumnGap: '1rem',
    padding: '0.5rem 1rem 0.5rem 1rem',
});

const stopTypeItems = [
    {
        id: StopType.Origin,
        label: StopType.Origin,
    },
    {
        id: StopType.Destination,
        label: StopType.Destination,
    },
];

interface BulkModifyProps {
    drafts: Load[];
    onSubmit: () => void;
}

// this is a lot like cascade, but reason it's not reusing
// might be a (different) feature?
// cascade does not have an origin/destination mode
export const BulkModify: React.FC<BulkModifyProps> = (props) => {
    const { drafts, onSubmit } = props;
    const { data: locations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { mutate: saveDrafts } = useDraftsMutation();
    const { organizationId } = useUser();
    const { mutate: addTravelTimes } = useAddTravelTimes(organizationId);
    const queryClient = useQueryClient();
    const queryKey = OrgQueryKeys.resolve(organizationId, OrgQueryKeys.locationTravelTimes);
    const travelTimesCache = queryClient.getQueryData<TravelTimesCache>(([queryKey]));

    const [mode, setMode] = React.useState(stopTypeItems[1]);
    const [modifiedDrafts, setModifiedDrafts] = React.useState(drafts);
    const [selectedLocationId, setSelectedLocationId] = React.useState<number>(-1);
    const [requiredBegin, setRequiredBegin] = React.useState<string | null>(null);
    const [requiredEnd, setRequiredEnd] = React.useState<string | null>(null);
    const [checked, setChecked] = React.useState<boolean>(false);
    const [interval, setInterval] = React.useState<typeof CascadeIntervals[0]>(CascadeIntervals[3]);
    
    const allLoadsHaveAtLeastOneStopOfModeType = drafts.map(x => x.stops.filter(x => x.type === mode.id).length).every(x => Boolean(x));
    const noRequiredDatesSet = !requiredBegin && !requiredEnd;

    const handleModeChange = (mode: typeof stopTypeItems[number]) => {
        // reset internal state
        setModifiedDrafts(drafts);
        setSelectedLocationId(-1);
        setRequiredBegin(null);
        setRequiredEnd(null);
        setChecked(false);
        setInterval(CascadeIntervals[3]);
        
        setMode(mode);
    };

    const handleLocationChange = (locationId: number) => {
        setSelectedLocationId(locationId);

        setModifiedDrafts(prevModifiedDrafts => prevModifiedDrafts.map(modifiedDraft => {
            return {
                ...modifiedDraft,
                stops: modifiedDraft.stops.map(stop => {
                    if (stop.type === mode.id) {
                        return {
                            ...stop,
                            organizationLocationId: locationId,
                        }
                    } else {
                        return stop;
                    }
                })
            }
        }));
    };

    const handleRequiredBeginChange = (date: string | null) => {
        setRequiredBegin(date);

        setModifiedDrafts(prevModifiedDrafts => prevModifiedDrafts.map((modifiedDraft, i) => {
            const modRequiredBegin = checked ? moment(date).add(i * interval.id, 'minutes').toISOString() : date;

            return {
                ...modifiedDraft,
                stops: modifiedDraft.stops.map(stop => {
                    if (stop.type === mode.id) {
                        return {
                            ...stop,
                            requiredBegin: modRequiredBegin,
                        }
                    } else {
                        return stop;
                    }
                })
            }
        }));
    };

    const handleCascadeChange = () => {
        setModifiedDrafts(prevModifiedDrafts => {
            return prevModifiedDrafts.map((modifiedDraft, i) => {
                const modRequiredBegin = !checked && requiredBegin ? moment(requiredBegin).add(i * interval.id, 'minutes').toISOString() : requiredBegin;
                const modRequiredEnd = !checked && requiredEnd ? moment(requiredEnd).add(i * interval.id, 'minutes').toISOString() : requiredEnd;
            
                return {
                    ...modifiedDraft,
                    stops: modifiedDraft.stops.map(stop => {
                        if (stop.type === mode.id) {
                            return {
                                ...stop,
                                requiredBegin: modRequiredBegin || stop.requiredBegin,
                                requiredEnd: modRequiredEnd || stop.requiredEnd,
                            }
                        } else {
                            return stop;
                        }
                    })
                }
            });
        });

        setChecked(!checked);
    };

    const handleRequiredEndChange = (date: string | null) => {
        setRequiredEnd(date);

        setModifiedDrafts(prevModifiedDrafts => prevModifiedDrafts.map((modifiedDraft, i) => {
            const modRequiredEnd = checked ? moment(date).add(i * interval.id, 'minutes').toISOString() : date;

            return {
                ...modifiedDraft,
                stops: modifiedDraft.stops.map(stop => {
                    if (stop.type === mode.id) {
                        return {
                            ...stop,
                            requiredEnd: modRequiredEnd,
                        }
                    } else {
                        return stop;
                    }
                })
            }
        }));
    };

    const handleIntervalChange = (newInterval: typeof CascadeIntervals[number]) => {
        setInterval(newInterval);

        setModifiedDrafts(prevModifiedDrafts => prevModifiedDrafts.map((modifiedDraft, i) => {
            const modRequiredBegin = requiredBegin ? moment(requiredBegin).add(i * newInterval.id, 'minutes').toISOString() : null;
            const modRequiredEnd = requiredEnd ? moment(requiredEnd).add(i * newInterval.id, 'minutes').toISOString() : null;

            return {
                ...modifiedDraft,
                stops: modifiedDraft.stops.map(stop => {
                    if (stop.type === mode.id) {
                        return {
                            ...stop,
                            requiredBegin: modRequiredBegin || stop.requiredBegin,
                            requiredEnd: modRequiredEnd || stop.requiredEnd,
                        }
                    } else {
                        return stop;
                    }
                })
            }
        }));
    };

    const handleSubmit = () => {
        const locationPairsToUpdate = [];
        if (travelTimesCache) {
            for (const draft of modifiedDrafts) {

                const pairs = stopsToOrganizationLocationIdPairs(draft.stops);
                const unknowPairs = findUnknownPairsInCache(travelTimesCache, pairs);
                const filteredUnknownPairs = updatePointlessPairs(unknowPairs, travelTimesCache, queryClient, queryKey);
                
                if (filteredUnknownPairs.length) {
                    locationPairsToUpdate.push(...filteredUnknownPairs);
                }
            }
            
            if (locationPairsToUpdate.length) {
                addTravelTimes(locationPairsToUpdate);
            }
        }

        saveDrafts({ drafts: modifiedDrafts });
        onSubmit();
    };

    return (
        <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
                <div style={{ width: '20rem' }}>
                    <Select
                        label={L.stopType()}
                        list={stopTypeItems}
                        item={mode}
                        onChange={handleModeChange}
                        style={{ marginBottom: '4rem '}}
                    />
                    <AgisticsSelect
                        label={L.location()}
                        item={selectedLocationId}
                        items={createAgisticsSelectElements(locations, toSiteDisplayNameFromOrganizationLocation)}
                        onChange={handleLocationChange}
                    />
                    <AgisticsDateTimePicker
                        label={L.requiredBegin()}
                        value={requiredBegin}
                        onChange={handleRequiredBeginChange}
                    />
                    <AgisticsDateTimePicker
                        label={L.requiredEnd()}
                        value={requiredEnd}
                        onChange={handleRequiredEndChange}
                    />
                    <FormControlLabel
                        disabled={noRequiredDatesSet}
                        control={
                            <Switch
                                checked={checked}
                                onChange={handleCascadeChange}
                            />
                        }
                        label={L.cascade()}
                    />
                    {checked && <SelectCascadeInterval
                        item={interval}
                        onChange={handleIntervalChange}
                        style={{ width: '20%' }}
                    />}
                </div>
                <div style={{ minWidth: '54rem', margin: '1rem 0 1rem 0' }}>
                    <BulkModifyRow style={{ backgroundColor: AgisticsColors.lightGray }}>
                        <span>{L.load()}</span>
                        <span>{L.type()}</span>
                        <span>{L.site()}</span>
                        <span>{L.requiredBegin()}</span>
                        <span>{L.requiredEnd()}</span>
                    </BulkModifyRow>
                    {modifiedDrafts.map(draft => {
                        return draft.stops.filter(stop => stop.type === mode.id).map(stop => {
                            const location = locations.find(x => x.id === stop.organizationLocationId);

                            return (
                                <BulkModifyRow key={stop.id} style={{ borderBottom: `1px solid ${AgisticsColors.lightGray}`, borderLeft: `1px solid ${AgisticsColors.lightGray}`, borderRight: `1px solid ${AgisticsColors.lightGray}` }}>
                                    <span>{draft.id}</span>
                                    <span>{stop.type}</span>
                                    <span>{location ? toSiteDisplayName(location.name, location.description) : ''}</span>
                                    <span>{stop.requiredBegin && localDateTimeDisplay(stop.requiredBegin)}</span>
                                    <span>{stop.requiredEnd && localDateTimeDisplay(stop.requiredEnd)}</span>
                                </BulkModifyRow>
                            );
                        });
                    })}
                </div>
            </div>
            <ModalFooter>
                <div style={{ visibility: !allLoadsHaveAtLeastOneStopOfModeType && checked ? 'visible' : 'hidden', color: '#F44336' }}>
                    {L.bulkModifyErrorExplanation()}
                </div>
                <div>
                    <Button
                        variant='contained'
                        color='primary'
                        disabled={noRequiredDatesSet && selectedLocationId < 1}
                        onClick={handleSubmit}
                    >
                        {L.save()}
                    </Button>
                </div>
            </ModalFooter>
        </div>
    );
};
